import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { FiPlus, FiEdit, FiTrash2, FiImage, FiX, FiSearch, FiFilter, FiGrid, FiList, FiCheckCircle, FiXCircle, FiUploadCloud, FiRefreshCw } from 'react-icons/fi';

function MenuManagement() {
  const { alert, confirm } = useModal();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ 
    name: '', 
    description: '', 
    display_order: 0,
    image_url: '',
    image: null,
    imagePreview: null
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showImages, setShowImages] = useState(true); // Toggle for images
  const [categoryBulkUpdatingId, setCategoryBulkUpdatingId] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    is_vegetarian: false,
    is_dietary_common: false,
    is_apc: false,
    is_available: true,
    is_chefs_special: false,
    spice_level: 0,
    preparation_time: '',
    recipe: [],
    image_url: '',
    image: null,
    imagePreview: null
  });
  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [aiMenuImportEnabled, setAiMenuImportEnabled] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importJobs, setImportJobs] = useState([]);
  const [selectedImportJobId, setSelectedImportJobId] = useState('');
  const [importJob, setImportJob] = useState(null);
  const [importItems, setImportItems] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchCategories(), fetchItems(), fetchTenantModules()]);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!showImportModal || !selectedImportJobId) return;
    refreshImportJob(selectedImportJobId);
  }, [showImportModal, selectedImportJobId]);

  const fetchTenantModules = async () => {
    try {
      const response = await api.get('/api/tenant/modules');
      const enabledModules = response.data?.enabledModules || [];
      setAiMenuImportEnabled(enabledModules.includes('ai_menu_import'));
      const hasInventory = enabledModules.includes('inventory');
      setInventoryEnabled(hasInventory);
      if (hasInventory) {
        await fetchInventoryItems();
      } else {
        setInventoryItems([]);
      }
    } catch (error) {
      setAiMenuImportEnabled(false);
      setInventoryEnabled(false);
      setInventoryItems([]);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const response = await api.get('/api/inventory/items');
      setInventoryItems(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (error) {
      setInventoryItems([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/menu/categories');
      setCategories(Array.isArray(response.data?.categories) ? response.data.categories : []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/menu');
      setItems(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (error) {
      console.error('Failed to fetch items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        await alert('Please select an image file', 'Image Required', 'warning');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        await alert('Image size must be less than 5MB', 'File Too Large', 'warning');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setItemForm({
        ...itemForm,
        image: file,
        imagePreview: previewUrl
      });

      try {
        const formData = new FormData();
        formData.append('image', file);

        const response = await api.post('/api/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        setItemForm(prev => ({
          ...prev,
          image_url: response.data.url,
          imagePreview: response.data.url
        }));
      } catch (error) {
        console.error('Failed to upload image:', error);
        await alert('Failed to upload image. Please try again.', 'Upload Failed', 'error');
        setItemForm(prev => ({
          ...prev,
          image: null,
          imagePreview: null
        }));
      }
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!itemForm.name || !itemForm.price || !itemForm.category_id) {
        await alert('Name, price, and category are required', 'Validation Error', 'warning');
        return;
      }

      const price = parseFloat(itemForm.price);
      if (isNaN(price) || price <= 0) {
        await alert('Please enter a valid price', 'Invalid Price', 'warning');
        return;
      }

      const spiceLevel = itemForm.spice_level ? parseInt(itemForm.spice_level) : 0;
      const preparationTime = itemForm.preparation_time ? parseInt(itemForm.preparation_time) : null;
      const recipeEntries = inventoryEnabled
        ? (itemForm.recipe || [])
            .map((entry) => ({
              inventory_item_id: entry.inventory_item_id,
              quantity_per_item: parseFloat(entry.quantity_per_item)
            }))
            .filter((entry) => entry.inventory_item_id && Number.isFinite(entry.quantity_per_item) && entry.quantity_per_item > 0)
        : [];

      if (editingItem) {
        const updateData = {
          name: itemForm.name,
          description: itemForm.description || '',
          price: price,
          category_id: itemForm.category_id,
          image_url: itemForm.image_url || null,
          is_vegetarian: itemForm.is_vegetarian || false,
          is_dietary_common: itemForm.is_dietary_common || false,
          is_apc: itemForm.is_apc || false,
          is_available: itemForm.is_available !== false,
          is_chefs_special: itemForm.is_chefs_special || false,
          spice_level: spiceLevel,
          preparation_time: preparationTime,
          recipe: recipeEntries
        };
        await api.put(`/api/menu/items/${editingItem.id}`, updateData);
      } else {
        const createData = {
          categoryId: itemForm.category_id,
          name: itemForm.name,
          description: itemForm.description || '',
          price: price,
          imageUrl: itemForm.image_url || null,
          isVegetarian: itemForm.is_vegetarian || false,
          isDietaryCommon: itemForm.is_dietary_common || false,
          isApc: itemForm.is_apc || false,
          spiceLevel: spiceLevel,
          preparationTime: preparationTime,
          displayOrder: 0,
          isChefsSpecial: itemForm.is_chefs_special || false,
          recipe: recipeEntries
        };
        await api.post('/api/menu/items', createData);
      }

      fetchItems();
      setShowItemModal(false);
      resetItemForm();
    } catch (error) {
      console.error('Failed to save item:', error);
      await alert(error.response?.data?.error || 'Failed to save menu item', 'Error', 'error');
    }
  };

  const handleDeleteItem = async (id) => {
    const confirmed = await confirm('Are you sure you want to delete this item?', 'Delete Item', 'warning');
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/api/menu/items/${id}`);

      if (response.data?.archived) {
        setItems(prev => prev.map(item => (
          item.id === id ? { ...item, is_available: false } : item
        )));
        await alert(response.data.message, 'Item Archived', 'warning');
      } else {
        setItems(prev => prev.filter(item => item.id !== id));
      }

      fetchItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
      await alert('Failed to delete item', 'Error', 'error');
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      const newAvailability = !item.is_available;
      
      // Optimistically update the UI
      const updatedItems = items.map(i => 
        i.id === item.id ? { ...i, is_available: newAvailability } : i
      );
      setItems(updatedItems);
      
      // Make API call - send only the fields that need to be updated
      await api.put(`/api/menu/items/${item.id}`, {
        is_available: newAvailability
      });
      
      // Refresh to ensure consistency
      fetchItems();
    } catch (error) {
      console.error('Failed to update availability:', error);
      // Revert on error
      fetchItems();
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      await alert(`Failed to ${item.is_available ? 'mark as unavailable' : 'mark as available'}: ${errorMessage}`, 'Error', 'error');
    }
  };

  const handleToggleChefsSpecial = async (item) => {
    try {
      const newValue = !item.is_chefs_special;

      const updatedItems = items.map(i =>
        i.id === item.id ? { ...i, is_chefs_special: newValue } : i
      );
      setItems(updatedItems);

      await api.put(`/api/menu/items/${item.id}`, {
        is_chefs_special: newValue
      });

      fetchItems();
    } catch (error) {
      console.error("Failed to update Chef's Special flag:", error);
      fetchItems();
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      await alert(`Failed to ${item.is_chefs_special ? 'remove from' : 'mark as'} Chef's Special: ${errorMessage}`, 'Error', 'error');
    }
  };

  const handleMakeCategoryUnavailable = async (category) => {
    if (!category?.id) return;

    const itemsInCategory = items.filter((item) => item.category_id === category.id);
    const availableItems = itemsInCategory.filter((item) => item.is_available !== false);

    if (availableItems.length === 0) {
      await alert(`All items in ${category.name} are already unavailable.`, 'No Changes Needed', 'warning');
      return;
    }

    const confirmed = await confirm(
      `Make all ${availableItems.length} available item${availableItems.length > 1 ? 's' : ''} in ${category.name} unavailable?`,
      'Make Category Unavailable',
      'warning'
    );

    if (!confirmed) return;

    try {
      setCategoryBulkUpdatingId(category.id);

      // Optimistic update so the category count/state reflects immediately.
      setItems((prev) => prev.map((item) => (
        item.category_id === category.id ? { ...item, is_available: false } : item
      )));

      await Promise.all(
        availableItems.map((item) => api.put(`/api/menu/items/${item.id}`, {
          is_available: false
        }))
      );

      await fetchItems();
      await alert(`Marked ${availableItems.length} item${availableItems.length > 1 ? 's' : ''} in ${category.name} as unavailable.`, 'Category Updated', 'success');
    } catch (error) {
      console.error('Failed to make category unavailable:', error);
      await fetchItems();
      await alert(error.response?.data?.error || 'Failed to update all items in this category', 'Error', 'error');
    } finally {
      setCategoryBulkUpdatingId(null);
    }
  };

  const handleOpenImportModal = () => {
    setImportFiles([]);
    setImportJobs([]);
    setSelectedImportJobId('');
    setImportJob(null);
    setImportItems([]);
    setShowImportModal(true);
    fetchImportJobs();
  };

  const fetchImportJobs = async () => {
    try {
      const response = await api.get('/api/menu/import?limit=30');
      const jobs = Array.isArray(response.data?.jobs) ? response.data.jobs : [];
      setImportJobs(jobs);

      if (jobs.length === 0) {
        setSelectedImportJobId('');
        return;
      }

      if (!selectedImportJobId || !jobs.some((job) => job.id === selectedImportJobId)) {
        setSelectedImportJobId(jobs[0].id);
      }
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to load import history', 'Error', 'error');
    }
  };

  const refreshImportJob = async (jobId) => {
    if (!jobId) return;
    try {
      const response = await api.get(`/api/menu/import/${jobId}`);
      const refreshedJob = response.data?.job || null;
      setImportJob(refreshedJob);
      setImportItems(Array.isArray(response.data?.items) ? response.data.items : []);

      if (refreshedJob?.id) {
        setImportJobs((prev) => {
          const exists = prev.some((job) => job.id === refreshedJob.id);
          if (!exists) {
            return [refreshedJob, ...prev];
          }
          return prev.map((job) => (job.id === refreshedJob.id ? refreshedJob : job));
        });
      }
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to refresh import job', 'Error', 'error');
    }
  };

  const handleCreateImportJobs = async () => {
    try {
      if (!Array.isArray(importFiles) || importFiles.length === 0) {
        await alert('Please select at least one menu image', 'Image Required', 'warning');
        return;
      }

      const invalidFile = importFiles.find((file) => !file.type?.startsWith('image/'));
      if (invalidFile) {
        await alert('Only image files are allowed. Please remove non-image files.', 'Invalid File', 'warning');
        return;
      }

      setImporting(true);
      const uploadedJobs = [];

      for (const imageFile of importFiles) {
        const formData = new FormData();
        formData.append('image', imageFile);

        const response = await api.post('/api/menu/import/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const job = response.data?.job;
        if (job?.id) {
          uploadedJobs.push(job);
        }
      }

      if (uploadedJobs.length === 0) {
        throw new Error('No jobs were created from the selected images');
      }

      for (const job of uploadedJobs) {
        await api.post(`/api/menu/import/${job.id}/process`);
      }

      await fetchImportJobs();

      setSelectedImportJobId(uploadedJobs[0].id);
      await refreshImportJob(uploadedJobs[0].id);
      setImportFiles([]);
    } catch (error) {
      await alert(error.response?.data?.error || error.message || 'Failed to upload import image', 'Error', 'error');
    } finally {
      setImporting(false);
    }
  };

  const updateImportItemField = async (itemId, updates) => {
    if (!importJob?.id) return;
    try {
      await api.patch(`/api/menu/import/${importJob.id}/items/${itemId}`, updates);
      setImportItems((prev) => prev.map((item) => (
        item.id === itemId
          ? {
              ...item,
              reviewed_name: updates.reviewedName !== undefined ? updates.reviewedName : item.reviewed_name,
              reviewed_price: updates.reviewedPrice !== undefined ? updates.reviewedPrice : item.reviewed_price,
              reviewed_category: updates.reviewedCategory !== undefined ? updates.reviewedCategory : item.reviewed_category,
              is_approved: updates.isApproved !== undefined ? updates.isApproved : item.is_approved,
              is_rejected: updates.isRejected !== undefined ? updates.isRejected : item.is_rejected
            }
          : item
      )));
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to update import row', 'Error', 'error');
    }
  };

  const handlePublishImport = async () => {
    if (!importJob?.id) {
      await alert('No import job found', 'Error', 'error');
      return;
    }

    const approvedCount = importItems.filter(r => r.is_approved).length;
    const confirmed = await confirm(
      `Add ${approvedCount} approved dish${approvedCount !== 1 ? 'es' : ''} to your live menu? Rejected dishes will not be added.`,
      'Publish to Menu',
      'warning'
    );
    if (!confirmed) return;

    try {
      setImporting(true);
      const response = await api.post(`/api/menu/import/${importJob.id}/publish`);
      const summary = response.data?.summary || {};
      await refreshImportJob(importJob.id);
      await Promise.all([fetchCategories(), fetchItems()]);
      await alert(
        `🎉 ${summary.publishedCount || 0} dish${(summary.publishedCount || 0) !== 1 ? 'es' : ''} added to your menu!${summary.skippedCount > 0 ? ` (${summary.skippedCount} skipped)` : ''}`,
        'Menu Updated',
        'success'
      );
      setShowImportModal(false);
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to publish import job', 'Error', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleApproveHighConfidence = async () => {
    if (!importJob?.id || !Array.isArray(importItems) || importItems.length === 0) {
      await alert('No extracted rows available to approve', 'Nothing to Approve', 'warning');
      return;
    }

    // Approve ALL items that haven't been explicitly rejected
    const rowsToApprove = importItems.filter((row) => row.is_rejected !== true);

    if (rowsToApprove.length === 0) {
      await alert('All rows have been rejected. Nothing left to approve.', 'Nothing to Approve', 'warning');
      return;
    }

    try {
      setImporting(true);
      await Promise.all(rowsToApprove.map((row) =>
        api.patch(`/api/menu/import/${importJob.id}/items/${row.id}`, {
          isApproved: true,
          isRejected: false
        })
      ));

      await refreshImportJob(importJob.id);
      await alert(`✅ ${rowsToApprove.length} dish${rowsToApprove.length !== 1 ? 'es' : ''} approved! Click "Publish" to add them to your menu.`, 'All Approved', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to bulk approve rows', 'Error', 'error');
    } finally {
      setImporting(false);
    }
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      description: '',
      price: '',
      category_id: '',
      is_vegetarian: false,
      is_dietary_common: false,
      is_apc: false,
      is_available: true,
      is_chefs_special: false,
      spice_level: 0,
      preparation_time: '',
      recipe: [],
      image_url: '',
      image: null,
      imagePreview: null
    });
    setEditingItem(null);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        name: categoryForm.name,
        description: categoryForm.description,
        displayOrder: categoryForm.display_order,
        image_url: categoryForm.image_url
      };
      
      if (editingCategory) {
        await api.put(`/api/menu/categories/${editingCategory.id}`, formData);
      } else {
        await api.post('/api/menu/categories', formData);
      }
      fetchCategories();
      setShowCategoryModal(false);
      resetCategoryForm();
    } catch (error) {
      console.error('Failed to save category:', error);
      await alert('Failed to save category', 'Error', 'error');
    }
  };

  const handleDeleteCategory = async (id) => {
    // Check if category has items
    const itemsInCategory = items.filter(item => item.category_id === id);
    const itemCount = itemsInCategory.length;
    
    let confirmationMessage;
    if (itemCount > 0) {
      confirmationMessage = `This category contains ${itemCount} item${itemCount > 1 ? 's' : ''}. Deleting this category will unassign all items from it. Are you sure you want to delete this category?`;
    } else {
      confirmationMessage = 'Are you sure you want to delete this category?';
    }
    
    const confirmed = await confirm(confirmationMessage, 'Delete Category', 'warning');
    if (!confirmed) return;
    
    try {
      await api.delete(`/api/menu/categories/${id}`);
      fetchCategories();
      fetchItems(); // Refresh items to update category assignments
    } catch (error) {
      console.error('Failed to delete category:', error);
      await alert('Failed to delete category', 'Error', 'error');
    }
  };

  const handleCategoryImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        await alert('Please select an image file', 'Image Required', 'warning');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        await alert('Image size must be less than 5MB', 'File Too Large', 'warning');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setCategoryForm({
        ...categoryForm,
        image: file,
        imagePreview: previewUrl
      });

      try {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('type', 'category'); // Specify this is a category image

        const response = await api.post('/api/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        setCategoryForm(prev => ({
          ...prev,
          image_url: response.data.url,
          imagePreview: response.data.url
        }));
      } catch (error) {
        console.error('Failed to upload image:', error);
        await alert('Failed to upload image. Please try again.', 'Upload Failed', 'error');
        setCategoryForm(prev => ({
          ...prev,
          image: null,
          imagePreview: null
        }));
      }
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ 
      name: '', 
      description: '', 
      display_order: 0,
      image_url: '',
      image: null,
      imagePreview: null
    });
    setEditingCategory(null);
  };

  const addRecipeIngredientRow = () => {
    setItemForm((prev) => ({
      ...prev,
      recipe: [...(prev.recipe || []), { inventory_item_id: '', quantity_per_item: '' }]
    }));
  };

  const updateRecipeIngredientRow = (index, field, value) => {
    setItemForm((prev) => ({
      ...prev,
      recipe: (prev.recipe || []).map((entry, currentIndex) => (
        currentIndex === index
          ? { ...entry, [field]: value }
          : entry
      ))
    }));
  };

  const removeRecipeIngredientRow = (index) => {
    setItemForm((prev) => ({
      ...prev,
      recipe: (prev.recipe || []).filter((_, currentIndex) => currentIndex !== index)
    }));
  };

  const filteredItems = Array.isArray(items) ? items.filter(item => {
    const matchesCategory = !selectedCategory || item.category_id === selectedCategory;
    const matchesSearch = !searchTerm || 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  }) : [];

  const selectedCategoryName = categories.find(c => c.id === selectedCategory)?.name || 'All Items';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Menu Management</h1>
            <p className="text-sm text-gray-600">Manage your menu categories and items</p>
          </div>
          <div className="flex gap-2">
            {aiMenuImportEnabled && (
              <button
                onClick={handleOpenImportModal}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 font-medium shadow-sm transition-all"
              >
                <FiUploadCloud className="text-base" /> Auto Fill Menu
              </button>
            )}
            <button
              onClick={() => {
                setEditingCategory(null);
                resetCategoryForm();
                setShowCategoryModal(true);
              }}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 font-medium shadow-sm transition-all"
            >
              <FiPlus className="text-base" /> Category
            </button>
            <button
              onClick={() => {
                resetItemForm();
                setShowItemModal(true);
              }}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2 font-medium shadow-sm transition-all"
            >
              <FiPlus className="text-base" /> Add Item
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                placeholder="Search items by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white text-sm"
              />
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Grid view"
              >
                <FiGrid className="text-lg" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                title="List view"
              >
                <FiList className="text-lg" />
              </button>
            </div>
            
            {/* Image Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-medium">Images</span>
              <button
                onClick={() => setShowImages(!showImages)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 ${
                  showImages ? 'bg-gray-900' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={showImages}
                title={showImages ? 'Hide images' : 'Show images'}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showImages ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <FiFilter className="text-sm" /> Filter:
              </span>
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  !selectedCategory
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({filteredItems.length})
              </button>
              {Array.isArray(categories) && categories.map((cat) => {
                const catItemCount = items.filter(item => item.category_id === cat.id).length;
                return (
                  <div key={cat.id} className="relative group inline-block">
                    <button
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedCategory === cat.id
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {cat.name} ({catItemCount})
                    </button>
                    <div className="absolute left-0 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex gap-1 bg-white rounded-lg shadow-lg border border-gray-200 p-1 pointer-events-none group-hover:pointer-events-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                        setEditingCategory(cat);
                        setCategoryForm({
                          name: cat.name,
                          description: cat.description || '',
                          display_order: cat.display_order || 0,
                          image_url: cat.image_url || '',
                          image: null,
                          imagePreview: cat.image_url || null
                        });
                        setShowCategoryModal(true);
                        }}
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        title="Edit category"
                      >
                        <FiEdit className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(cat.id);
                        }}
                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                        title="Delete category"
                      >
                        <FiTrash2 className="text-sm" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMakeCategoryUnavailable(cat);
                        }}
                        disabled={categoryBulkUpdatingId === cat.id}
                        className="p-1.5 text-amber-600 hover:text-amber-900 hover:bg-amber-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Make all items unavailable"
                      >
                        {categoryBulkUpdatingId === cat.id ? <FiRefreshCw className="text-sm animate-spin" /> : <FiXCircle className="text-sm" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Menu Items Display */}
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-pulse">
              <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500 font-medium">Loading menu items...</p>
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-16 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiImage className="text-4xl text-gray-400" />
            </div>
            <p className="text-gray-700 text-xl font-semibold mb-2">No menu items found</p>
            <p className="text-gray-500 text-sm mb-6">
              {searchTerm ? 'Try a different search term' : selectedCategory ? 'No items in this category' : 'Add your first menu item to get started'}
            </p>
            {!searchTerm && !selectedCategory && (
              <button
                onClick={() => {
                  resetItemForm();
                  setShowItemModal(true);
                }}
                className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 flex items-center gap-2 mx-auto font-medium shadow-sm transition-all"
              >
                <FiPlus /> Add First Item
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-200 group flex flex-col"
              >
                {showImages && (
                  <div className="relative h-32 bg-gray-100 overflow-hidden rounded-t-lg">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 ${item.image_url ? 'hidden' : ''}`}
                      style={{ display: item.image_url ? 'none' : 'flex' }}
                    >
                      <FiImage className="text-2xl text-gray-400" />
                    </div>
                    
                    {/* Availability & Chef's Special Checkboxes - Top Left */}
                    <div className="absolute top-2.5 left-2.5 z-10 flex flex-col gap-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.is_available}
                          onChange={() => handleToggleAvailability(item)}
                          className="w-5 h-5 rounded border-2 border-white bg-white text-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer shadow-md"
                          style={{
                            accentColor: '#111827'
                          }}
                          title={item.is_available ? 'Available - Click to mark unavailable' : 'Unavailable - Click to mark available'}
                        />
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.is_chefs_special}
                          onChange={() => handleToggleChefsSpecial(item)}
                          className="w-5 h-5 rounded border-2 border-white bg-white text-yellow-500 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 cursor-pointer shadow-md"
                          style={{
                            accentColor: '#facc15'
                          }}
                          title={item.is_chefs_special ? "Chef's Special - Click to remove" : "Mark as Chef's Special"}
                        />
                      </label>
                    </div>
                  </div>
                )}
                
                {!showImages && (
                  <div className="relative p-3.5 border-b border-gray-200 bg-gray-50">
                    {/* Availability & Chef's Special Checkboxes - Top Left (when images hidden) */}
                    <div className="absolute top-3.5 left-3.5 z-10 flex flex-col gap-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.is_available}
                          onChange={() => handleToggleAvailability(item)}
                          className="w-5 h-5 rounded border-2 border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer shadow-sm"
                          style={{
                            accentColor: '#111827'
                          }}
                          title={item.is_available ? 'Available - Click to mark unavailable' : 'Unavailable - Click to mark available'}
                        />
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.is_chefs_special}
                          onChange={() => handleToggleChefsSpecial(item)}
                          className="w-5 h-5 rounded border-2 border-gray-300 bg-white text-yellow-500 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 cursor-pointer shadow-sm"
                          style={{
                            accentColor: '#facc15'
                          }}
                          title={item.is_chefs_special ? "Chef's Special - Click to remove" : "Mark as Chef's Special"}
                        />
                      </label>
                    </div>
                  </div>
                )}
                
                <div className="p-3.5 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-2.5 min-h-[2.75rem]">
                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 break-words leading-tight">{item.name}</h3>
                      {item.is_apc && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                          APC
                        </span>
                      )}
                    </div>
                    {!item.is_available && (
                      <span className="text-[10px] text-red-600 font-medium ml-2 flex-shrink-0 whitespace-nowrap">Unavail</span>
                    )}
                  </div>
                  
                  <div className="mt-auto">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-base font-bold text-gray-900">₹{parseFloat(item.price).toFixed(2)}</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingItem(item);
                          setItemForm({
                            name: item.name || '',
                            description: item.description || '',
                            price: item.price || '',
                            category_id: item.category_id || '',
                            is_vegetarian: item.is_vegetarian || false,
                            is_dietary_common: item.is_dietary_common || false,
                            is_apc: item.is_apc || false,
                            is_available: item.is_available !== false,
                            is_chefs_special: item.is_chefs_special || false,
                            spice_level: item.spice_level || 0,
                            preparation_time: item.preparation_time ? String(item.preparation_time) : '',
                            recipe: Array.isArray(item.recipe)
                              ? item.recipe.map((entry) => ({
                                  inventory_item_id: entry.inventory_item_id,
                                  quantity_per_item: entry.quantity_per_item
                                }))
                              : [],
                            image_url: item.image_url || '',
                            image: null,
                            imagePreview: item.image_url || null
                          });
                          setShowItemModal(true);
                        }}
                        className="bg-gray-900 text-white py-2 px-2.5 rounded-lg hover:bg-gray-800 flex items-center justify-center transition-all shadow-sm hover:shadow"
                        title="Edit item"
                      >
                        <FiEdit className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="bg-red-50 text-red-700 py-2 px-2.5 rounded-lg hover:bg-red-100 transition-all border border-red-200 shadow-sm hover:shadow"
                        title="Delete item"
                      >
                        <FiTrash2 className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="divide-y divide-gray-100">
              {filteredItems.map((item) => (
                <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    {/* Availability Checkbox - Left Side */}
                    <div className="flex-shrink-0">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.is_available}
                          onChange={() => handleToggleAvailability(item)}
                          className="w-5 h-5 rounded border-2 border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 cursor-pointer"
                          style={{
                            accentColor: '#111827'
                          }}
                          title={item.is_available ? 'Available - Click to mark unavailable' : 'Unavailable - Click to mark available'}
                        />
                      </label>
                    </div>
                    
                    {/* Image */}
                    <div className="w-20 h-20 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-full h-full flex items-center justify-center ${item.image_url ? 'hidden' : ''}`}
                        style={{ display: item.image_url ? 'none' : 'flex' }}
                      >
                        <FiImage className="text-2xl text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 text-base break-words">{item.name}</h3>
                            {!item.is_available && (
                              <span className="text-xs text-red-600 font-medium flex-shrink-0">(Unavailable)</span>
                            )}
                            {item.is_apc && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                                APC
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                    <div className="flex items-center gap-4 mt-2">
                        <span className="text-lg font-bold text-gray-900">₹{parseFloat(item.price).toFixed(2)}</span>
                        <span className="text-xs text-gray-500">
                          {categories.find(c => c.id === item.category_id)?.name || 'Uncategorized'}
                        </span>
                      {item.is_chefs_special && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-800">
                          Chef's Special
                        </span>
                      )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => {
                          setEditingItem(item);
                          setItemForm({
                            name: item.name || '',
                            description: item.description || '',
                            price: item.price || '',
                            category_id: item.category_id || '',
                            is_vegetarian: item.is_vegetarian || false,
                            is_dietary_common: item.is_dietary_common || false,
                            is_apc: item.is_apc || false,
                            is_available: item.is_available !== false,
                            is_chefs_special: item.is_chefs_special || false,
                            spice_level: item.spice_level || 0,
                            preparation_time: item.preparation_time ? String(item.preparation_time) : '',
                            recipe: Array.isArray(item.recipe)
                              ? item.recipe.map((entry) => ({
                                  inventory_item_id: entry.inventory_item_id,
                                  quantity_per_item: entry.quantity_per_item
                                }))
                              : [],
                            image_url: item.image_url || '',
                            image: null,
                            imagePreview: item.image_url || null
                          });
                          setShowItemModal(true);
                        }}
                        className="bg-gray-900 text-white px-2 py-2 rounded-lg hover:bg-gray-800 flex items-center justify-center text-sm font-medium transition-all"
                        title="Edit item"
                      >
                        <FiEdit className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="bg-red-50 text-red-700 px-2 py-2 rounded-lg hover:bg-red-100 transition-all border border-red-200"
                        title="Delete item"
                      >
                        <FiTrash2 className="text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Import Modal — Simplified */}
        {showImportModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onMouseDown={() => { if (!importing) setShowImportModal(false); }}
          >
            <div
              className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl flex flex-col"
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FiUploadCloud className="text-gray-700" /> Menu Photo Import
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">Upload menu photos → review dishes → publish to menu</p>
                </div>
                <button
                  onClick={() => { if (!importing) setShowImportModal(false); }}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Upload Section */}
                {importItems.length === 0 && (
                  <div className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${importFiles.length > 0 ? 'border-gray-400 bg-gray-50' : 'border-gray-200 bg-gray-50 hover:border-gray-400'}`}>
                    <FiUploadCloud size={40} className="mx-auto text-gray-400 mb-3" />
                    <p className="text-lg font-semibold text-gray-700 mb-1">Upload menu photo(s)</p>
                    <p className="text-sm text-gray-400 mb-4">We'll automatically extract all dishes, prices and categories</p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setImportFiles(Array.from(e.target.files || []))}
                        className="hidden"
                        disabled={importing}
                      />
                      <span className="inline-block px-5 py-2.5 border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all">
                        Choose Files
                      </span>
                    </label>
                    {importFiles.length > 0 && (
                      <div className="mt-4 space-y-1">
                        {importFiles.map((file, i) => (
                          <div key={i} className="text-sm text-gray-600 flex items-center justify-center gap-2">
                            <FiCheckCircle className="text-green-500" size={14} /> {file.name}
                          </div>
                        ))}
                        <button
                          onClick={handleCreateImportJobs}
                          disabled={importing}
                          className="mt-4 px-8 py-3 bg-gray-900 text-white rounded-xl font-semibold text-sm hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 mx-auto"
                        >
                          <FiUploadCloud />
                          {importing ? 'Scanning menu...' : `Scan ${importFiles.length} photo${importFiles.length > 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Processing state */}
                {importing && importItems.length === 0 && (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Scanning your menu photos with AI...</p>
                    <p className="text-sm text-gray-400 mt-1">This usually takes 10–20 seconds</p>
                  </div>
                )}

                {/* Dishes review table */}
                {importItems.length > 0 && (
                  <>
                    {/* Stats + actions bar */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-base font-bold text-gray-900">{importItems.length} dishes found</span>
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {importItems.filter(r => r.is_approved).length} approved
                        </span>
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          {importItems.filter(r => r.is_rejected).length} rejected
                        </span>
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {importItems.filter(r => !r.is_approved && !r.is_rejected).length} pending
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleApproveHighConfidence}
                          disabled={importing}
                          className="px-4 py-2 rounded-xl text-sm font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5 transition-all"
                        >
                          <FiCheckCircle size={15} /> Approve All
                        </button>
                        <button
                          onClick={() => { setImportItems([]); setImportFiles([]); setImportJob(null); }}
                          disabled={importing}
                          className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-all"
                        >
                          Upload New
                        </button>
                      </div>
                    </div>

                    <p className="text-xs text-gray-400 -mt-2">Edit name, price or category if needed. Then approve or reject each dish.</p>

                    {/* Table */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="text-left px-4 py-3 font-semibold text-gray-700">Dish Name</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-700 w-28">Price (₹)</th>
                            <th className="text-left px-4 py-3 font-semibold text-gray-700 w-36">Category</th>
                            <th className="text-center px-4 py-3 font-semibold text-gray-700 w-36">Decision</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {importItems.map((row) => {
                            const isPending = !row.is_approved && !row.is_rejected;
                            const rowBg = row.is_approved ? 'bg-green-50' : row.is_rejected ? 'bg-red-50 opacity-60' : (typeof row.confidence === 'number' && row.confidence < 0.75) ? 'bg-amber-50' : 'bg-white';
                            return (
                              <tr key={row.id} className={`${rowBg} transition-colors`}>
                                <td className="px-4 py-2.5">
                                  <input
                                    type="text"
                                    defaultValue={row.reviewed_name || row.extracted_name || ''}
                                    onBlur={(e) => updateImportItemField(row.id, { reviewedName: e.target.value })}
                                    disabled={row.is_rejected}
                                    className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-gray-400 disabled:opacity-40 disabled:bg-gray-100"
                                  />
                                </td>
                                <td className="px-4 py-2.5">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    defaultValue={row.reviewed_price ?? row.extracted_price ?? ''}
                                    onBlur={(e) => updateImportItemField(row.id, { reviewedPrice: e.target.value })}
                                    disabled={row.is_rejected}
                                    className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-gray-400 disabled:opacity-40 disabled:bg-gray-100"
                                  />
                                </td>
                                <td className="px-4 py-2.5">
                                  <input
                                    type="text"
                                    defaultValue={row.reviewed_category || row.suggested_category || 'Others'}
                                    onBlur={(e) => updateImportItemField(row.id, { reviewedCategory: e.target.value })}
                                    disabled={row.is_rejected}
                                    className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-gray-400 disabled:opacity-40 disabled:bg-gray-100"
                                  />
                                </td>
                                <td className="px-4 py-2.5">
                                  <div className="flex gap-1.5 justify-center">
                                    <button
                                      onClick={() => updateImportItemField(row.id, { isApproved: true, isRejected: false })}
                                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1 ${row.is_approved ? 'bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700'}`}
                                    >
                                      <FiCheckCircle size={13} /> Add
                                    </button>
                                    <button
                                      onClick={() => updateImportItemField(row.id, { isApproved: false, isRejected: true })}
                                      className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1 ${row.is_rejected ? 'bg-red-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-700'}`}
                                    >
                                      <FiXCircle size={13} /> Skip
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Publish footer */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-gray-100">
                      <div className="text-sm text-gray-500">
                        <span className="font-semibold text-gray-800">{importItems.filter(r => r.is_approved).length}</span> dishes will be added to your menu
                      </div>
                      <button
                        onClick={handlePublishImport}
                        disabled={importing || !importJob?.id || importItems.filter(r => r.is_approved).length === 0}
                        className="px-8 py-3 bg-green-600 text-white rounded-xl font-bold text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-sm"
                      >
                        <FiCheckCircle size={16} />
                        {importing ? 'Publishing...' : `Publish ${importItems.filter(r => r.is_approved).length} Dishes to Menu`}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {showCategoryModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onMouseDown={() => {
              setShowCategoryModal(false);
              resetCategoryForm();
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingCategory ? 'Edit Category' : 'Add Category'}
                </h2>
                <button
                  onClick={() => {
                    setShowCategoryModal(false);
                    resetCategoryForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                    placeholder="e.g., Appetizers, Main Course"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none"
                    rows="3"
                    placeholder="Optional description for this category"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    value={categoryForm.display_order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in the menu</p>
                </div>
                
                {/* Category Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Image (for QR Menu)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">Upload an image file from your computer (JPG, PNG, GIF - Max 5MB)</p>
                  {(categoryForm.imagePreview || categoryForm.image_url) && (
                    <div className="mb-4">
                      <div className="relative inline-block">
                        <img
                          src={categoryForm.imagePreview || categoryForm.image_url}
                          alt="Category preview"
                          className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {categoryForm.image_url && (
                          <div className="absolute -bottom-2 left-0 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold shadow-md">
                            ✓ Uploaded
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryForm({
                              ...categoryForm,
                              image: null,
                              imagePreview: null,
                              image_url: ''
                            });
                          }}
                          className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCategoryImageChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-900 file:text-white hover:file:bg-gray-800 file:cursor-pointer"
                  />
                  <p className="text-xs text-gray-400 mt-2">Image will be uploaded automatically from your computer</p>
                  <p className="text-xs text-gray-500 mt-1">This image will be displayed in the QR menu category view</p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 font-medium transition-all"
                  >
                    {editingCategory ? 'Update' : 'Add'} Category
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCategoryModal(false);
                      resetCategoryForm();
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 font-medium transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Item Modal */}
        {showItemModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onMouseDown={() => {
              setShowItemModal(false);
              resetItemForm();
            }}
          >
            <div
              className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
                </h2>
                <button
                  onClick={() => {
                    setShowItemModal(false);
                    resetItemForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
              <form onSubmit={handleItemSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Name *</label>
                    <input
                      type="text"
                      value={itemForm.name}
                      onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                      placeholder="e.g., Spring Rolls"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={itemForm.price || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItemForm({ ...itemForm, price: val === '' ? '' : val });
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={itemForm.category_id}
                    onChange={(e) => setItemForm({ ...itemForm, category_id: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all bg-white"
                    required
                  >
                    <option value="">Select a category</option>
                    {Array.isArray(categories) && categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none"
                    rows="3"
                    placeholder="Describe the item (optional)"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preparation Time (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={itemForm.preparation_time || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItemForm({ ...itemForm, preparation_time: val === '' ? '' : val });
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                      placeholder="e.g., 15"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Spice Level (0-5)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={itemForm.spice_level || 0}
                      onChange={(e) => {
                        const val = e.target.value;
                        const numVal = val === '' ? 0 : Math.max(0, Math.min(5, parseInt(val) || 0));
                        setItemForm({ ...itemForm, spice_level: numVal });
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setItemForm({ ...itemForm, is_vegetarian: !itemForm.is_vegetarian })}>
                    <input
                      type="checkbox"
                      checked={itemForm.is_vegetarian}
                      onChange={(e) => setItemForm({ ...itemForm, is_vegetarian: e.target.checked })}
                      className="w-4 h-4 text-gray-900 focus:ring-gray-900"
                    />
                    <label className="text-sm font-medium text-gray-700 cursor-pointer">🥬 Vegetarian</label>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setItemForm({ ...itemForm, is_dietary_common: !itemForm.is_dietary_common })}>
                    <input
                      type="checkbox"
                      checked={itemForm.is_dietary_common}
                      onChange={(e) => setItemForm({ ...itemForm, is_dietary_common: e.target.checked })}
                      className="w-4 h-4 text-gray-900 focus:ring-gray-900"
                    />
                    <label className="text-sm font-medium text-gray-700 cursor-pointer">🔁 Common (Both)</label>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setItemForm({ ...itemForm, is_available: !itemForm.is_available })}>
                    <input
                      type="checkbox"
                      checked={itemForm.is_available}
                      onChange={(e) => setItemForm({ ...itemForm, is_available: e.target.checked })}
                      className="w-4 h-4 text-gray-900 focus:ring-gray-900"
                    />
                    <label className="text-sm font-medium text-gray-700 cursor-pointer">✓ Available</label>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setItemForm({ ...itemForm, is_chefs_special: !itemForm.is_chefs_special })}>
                    <input
                      type="checkbox"
                      checked={itemForm.is_chefs_special}
                      onChange={(e) => setItemForm({ ...itemForm, is_chefs_special: e.target.checked })}
                      className="w-4 h-4 text-yellow-500 focus:ring-yellow-500"
                    />
                    <label className="text-sm font-medium text-gray-700 cursor-pointer">⭐ Chef's Special</label>
                  </div>
                  <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setItemForm({ ...itemForm, is_apc: !itemForm.is_apc })}>
                    <input
                      type="checkbox"
                      checked={itemForm.is_apc}
                      onChange={(e) => setItemForm({ ...itemForm, is_apc: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label className="text-sm font-medium text-gray-700 cursor-pointer">APC Item</label>
                  </div>
                </div>

                {inventoryEnabled && (
                  <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50/60">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Recipe (Inventory Deduction)</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Define raw ingredients used for 1 quantity of this menu item.</p>
                      </div>
                      <button
                        type="button"
                        onClick={addRecipeIngredientRow}
                        className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-800 shadow-sm transition-colors"
                      >
                        + Ingredient
                      </button>
                    </div>

                    {(itemForm.recipe || []).length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-xs text-gray-500">
                        No recipe set. Orders of this item won’t deduct inventory.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(itemForm.recipe || []).map((entry, index) => (
                          <div key={`recipe-${index}`} className="grid grid-cols-12 gap-2 items-center bg-white border border-gray-200 rounded-lg p-2.5">
                            <select
                              value={entry.inventory_item_id || ''}
                              onChange={(e) => updateRecipeIngredientRow(index, 'inventory_item_id', e.target.value)}
                              className="col-span-7 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white"
                            >
                              <option value="">Select inventory item</option>
                              {inventoryItems.map((inventoryItem) => (
                                <option key={inventoryItem.id} value={inventoryItem.id}>
                                  {inventoryItem.name} ({inventoryItem.unit})
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              placeholder="Qty"
                              value={entry.quantity_per_item ?? ''}
                              onChange={(e) => updateRecipeIngredientRow(index, 'quantity_per_item', e.target.value)}
                              className="col-span-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                            <button
                              type="button"
                              onClick={() => removeRecipeIngredientRow(index)}
                              className="col-span-2 bg-red-50 text-red-700 py-2 rounded-lg hover:bg-red-100 text-xs font-semibold border border-red-200 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Item Image</label>
                  <p className="text-xs text-gray-500 mb-3">Upload an image file (JPG, PNG, GIF - Max 5MB)</p>
                  {(itemForm.imagePreview || itemForm.image_url) && (
                    <div className="mb-4">
                      <div className="relative inline-block">
                        <img
                          src={itemForm.imagePreview || itemForm.image_url}
                          alt="Preview"
                          className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {itemForm.image_url && (
                          <div className="absolute -bottom-2 left-0 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold shadow-md">
                            ✓ Uploaded
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-900 file:text-white hover:file:bg-gray-800 file:cursor-pointer"
                  />
                  <p className="text-xs text-gray-400 mt-2">Image will be uploaded automatically</p>
                </div>
                <div className="flex gap-3 pt-2 border-t border-gray-200">
                  <button
                    type="submit"
                    className="flex-1 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-gray-800 font-medium transition-all"
                  >
                    {editingItem ? 'Update' : 'Add'} Item
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowItemModal(false);
                      resetItemForm();
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 font-medium transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MenuManagement;
